import { Card, Button, Spinner, Alert, ListGroup } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRedo } from '@fortawesome/free-solid-svg-icons'
import { useAdminUserAiSummary, useRefreshAdminUserAiSummary } from 'api/hooks/use-admin-user'
import AppBadge from 'shared/badges/app-badge'
import { errorMessage } from 'admin/shared/errors'
import { formatDateTime } from './user-activity-format'

// How the model rated the user's engagement over the window. `minimal` covers
// both a quiet month and a user who has never signed in at all.
const ENGAGEMENT = {
  heavy: { variant: 'success', label: 'Heavy use' },
  regular: { variant: 'success', label: 'Regular use' },
  occasional: { variant: 'warning', label: 'Occasional use' },
  minimal: { variant: 'secondary', label: 'Minimal use' }
}

const EngagementBadge = ({ engagement }) => {
  const level = ENGAGEMENT[engagement]
  if (!level) return null

  return <AppBadge variant={level.variant} className="ms-2">{level.label}</AppBadge>
}

const Provenance = ({ summary }) => (
  <div className="small text-muted mt-3">
    {`Based on ${summary.events_analyzed?.toLocaleString() ?? 0} tracked events across `}
    {`${summary.logins_analyzed?.toLocaleString() ?? 0} logins in the last ${summary.window_days} days`}
    {summary.generated_at && ` · generated ${formatDateTime(summary.generated_at)}`}
  </div>
)

const UserAiActivitySummary = ({ userId }) => {
  const { data: summary, isLoading, error } = useAdminUserAiSummary({ userId })
  const refresh = useRefreshAdminUserAiSummary({ userId })

  const header = (
    <div className="d-flex justify-content-between align-items-center">
      <div>
        <AppBadge variant="ai">AI</AppBadge>
        <span className="ms-2 fw-bold">Activity Summary</span>
        {summary && <EngagementBadge engagement={summary.engagement} />}
      </div>
      <Button
        variant="light"
        size="sm"
        className="text-primary"
        onClick={() => refresh.mutate()}
        disabled={isLoading || refresh.isLoading}
      >
        {refresh.isLoading
          ? <><Spinner size="sm" animation="border" className="me-1" />Regenerating</>
          : <><FontAwesomeIcon icon={faRedo} className="me-1" />Regenerate</>}
      </Button>
    </div>
  )

  return (
    <Card className="mb-4">
      <Card.Header>{header}</Card.Header>
      <Card.Body>
        {isLoading && (
          <div className="text-muted">
            <Spinner size="sm" animation="border" className="me-2" />
            Reading this user&apos;s recent activity...
          </div>
        )}

        {!isLoading && (error || refresh.error) && (
          <Alert variant="warning" className="mb-0">
            {errorMessage(error || refresh.error, 'Could not generate an activity summary.')}
          </Alert>
        )}

        {!isLoading && !error && summary && (
          <>
            {summary.headline && <h5 className="mb-2">{summary.headline}</h5>}
            <p className="mb-0">{summary.summary}</p>

            {summary.highlights?.length > 0 && (
              <ListGroup variant="flush" className="mt-3">
                {summary.highlights.map((highlight) => (
                  <ListGroup.Item key={highlight} className="px-0 py-1 border-0">
                    • {highlight}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}

            <Provenance summary={summary} />
          </>
        )}
      </Card.Body>
    </Card>
  )
}

export default UserAiActivitySummary
